import { apiClient } from './api/apiClient';
import { User, PagedResponse, ApiResponse, UserRole } from '../types';

interface CreateUserDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: UserRole;
  companyId?: number;
  siteId?: number;
}

interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  role?: UserRole;
  companyId?: number;
  siteId?: number;
  isActive?: boolean;
}

interface UserFilter {
  role?: UserRole;
  companyId?: number;
  siteId?: number;
  isActive?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
}

class UserService {
  /**
   * Ottieni lista utenti
   */
  async getUsers(filter?: UserFilter): Promise<PagedResponse<User>> {
    const params = new URLSearchParams();

    if (filter) {
      if (filter.role !== undefined) params.append('role', filter.role.toString());
      if (filter.companyId) params.append('companyId', filter.companyId.toString());
      if (filter.siteId) params.append('siteId', filter.siteId.toString());
      if (filter.isActive !== undefined) params.append('isActive', filter.isActive.toString());
      if (filter.search) params.append('search', filter.search);
      if (filter.page) params.append('page', filter.page.toString());
      if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
    }

    return await apiClient.get<PagedResponse<User>>(
      `/users?${params.toString()}`
    );
  }

  /**
   * Ottieni dettagli utente per ID
   */
  async getUserById(id: number): Promise<User> {
    const response = await apiClient.get<ApiResponse<User>>(`/users/${id}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Utente non trovato');
  }

  /**
   * Crea nuovo utente
   */
  async createUser(data: CreateUserDto): Promise<User> {
    const response = await apiClient.post<ApiResponse<User>>('/users', data);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Errore nella creazione dell\'utente');
  }

  /**
   * Aggiorna utente esistente
   */
  async updateUser(id: number, data: UpdateUserDto): Promise<User> {
    const response = await apiClient.put<ApiResponse<User>>(
      `/users/${id}`,
      data
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Errore nell\'aggiornamento dell\'utente');
  }

  /**
   * Disattiva utente
   */
  async deactivateUser(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  }
}

// Export singleton instance
export const userService = new UserService();
